// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the pins and the graph in the Tools panel's Data tab
 * (docs/AYNI_TOOLS_DATA_PLAN.md, Phase 3).
 *
 * "Pin on globe" turns pin mode on: each click on the globe drops a pin
 * (four at most) and a pin can be dragged to move it. Each row shows the
 * pin's colour, its latest value with units, where it is, and ✕. "Mark
 * highest & lowest" drops two pins on this frame's extremes, which follow
 * them as the data changes (AYNI, owner 2026-10-18: "Follow as the data
 * changes", "Show track"), and "Clear all" lifts every pin. Under the
 * rows, one SVG graph draws a trace per pin against a shared y-axis over the dataset's window, with a coverage rail
 * per pin (filled where a frame was read, hatched where it held no data),
 * since the graph fills in as the dataset plays.
 *
 * Renders into `#tools-data-pins`. The rows and buttons are rebuilt only
 * when the pins, the dataset's readability or pin mode change; a new
 * reading updates the values and the graph in place, so a pointer or the
 * focus on a ✕ is never pulled out from under the viewer.
 *
 * The store, the globe and the frame are main.ts's; this module only
 * draws and asks.
 *
 * AYNI (2026-10-04): the graph can leave the panel. Dragged out (by its
 * ⇱ button on touch) or popped out with a press on ⇱, it becomes a window
 * over the globe (`pinGraphWindow.ts`) drawn at that window's size, and
 * the panel keeps a note in its place, which is also where a drop puts it
 * back.
 *
 * AYNI (2026-10-04, owner's ask): a green line on the graph follows
 * playback, so on a second lap, when most of the graph is drawn, the
 * viewer still sees where in time the globe is. Over the graph a dashed
 * line follows the pointer with the time under it (and, in the window,
 * each pin's value there); a click there moves playback to that time.
 *
 * AYNI (owner, 2026-10-09): "Click to play from here" is drawn beside the
 * hover line, on the side away from the time and values (left of the line
 * while they fit on its right), not as the browser's tooltip, which sat
 * on top of them. A pin's name is a button: it opens a field in its place,
 * and the name given is the pin's everywhere (`pinNames.ts`). The values
 * under the hover line go to `scrub`, so the pins on a paused globe show
 * them as the pointer moves.
 */

import { t, tAttr, tHtml, type MessageKey } from '../i18n'
import { formatDate, formatNumber } from '../i18n/format'
import { escapeAttr, escapeHtml } from './domUtils'
import { SURFACE_MODES, surfaceLabelKey, type SurfaceMode } from '../services/measure/surfaceMask'
import { MAX_PINS, PIN_NAME_MAX, type Pin, type PinReading, type PinStore } from '../services/measure/pins'
import { pinEnglishName, pinName } from '../services/measure/pinNames'
import { buildSeriesChart, type ChartGeometry } from '../services/measure/chartGeometry'
import { seriesCoverage, type MeasurementSeries } from '../services/measure/series'
import { seriesWhenExtremes, type SeriesWhen } from '../services/measure/whenExtremes'
import { createPinGraphWindow, type DockReason, type PinGraphWindow } from './pinGraphWindow'
import { buildPinStatsCsvText, buildPinsCsvText, pinsCsvFilename } from './pinsCsv'
import type { BundleCsv } from '../services/research/bundle'
import {
  correlationBlockHtml,
  correlationCsvRows,
  defaultYearPeriod,
  emergenceControlsHtml,
  emergenceCsvRows,
  emergenceResultsHtml,
  emergenceSvg,
  statsCounts,
  trendBlockHtml,
  trendCsvRows,
  trendLinesSvg,
  type EmergenceView,
  type YearPeriod,
} from './pinTrendUI'
import { pinEmergence, statsStepMs } from '../services/research/pinStats'
import { downloadCsv } from './analyzeExport'
import {
  referenceLabel,
  referenceLinesVisible,
  referenceNote,
  onReferenceLinesChange,
  referenceShort,
  setReferenceLinesVisible,
  type ReferenceLine,
} from '../services/referenceLines'
import { trail, trailOn } from '../services/research/researchTrail'

/**
 * AYNI: the methods as the research record writes them (English, like the
 * CSV files), with their sources (research tier, slice 4).
 */
const RECORD_METHOD: Record<'trend' | 'emergence' | 'correlation', string> = {
  trend: 'Least squares with its interval widened for lag-1 autocorrelation (Santer et al. 2000; effective n after Nychka et al. 2000) and the Mann-Kendall test with tie correction and the Hamed & Rao (1998) factor, with Sen slope and Gilbert interval; a rising or falling verdict only when both agree at 5 %', // i18n-exempt: research record, English like the CSV
  emergence: 'Time of emergence after Hawkins & Sutton (2012): the first year after which the centred 10-year running mean stays beyond k standard deviations of the baseline period, sigma after removing the baseline period\'s own linear trend, never below one quantisation step', // i18n-exempt: research record, English like the CSV
  correlation: 'Pearson and Spearman correlation with the effective number of independent values after Bretherton et al. (1999), also after removing both linear trends; the strongest lag with a Bonferroni-corrected p', // i18n-exempt: research record, English like the CSV
}

/** What the globe can be read for. */
/** AYNI: 'plan' — the data carries values, but the plan has no data tools. */
export type PinReadability = 'none' | 'picture' | 'release' | 'encoded' | 'plan'

export interface DataPinsDeps {
  store: PinStore
  readability(): PinReadability
  /** A reading with its units, as the hover readout prints it. */
  format(reading: PinReading): string
  /** A value on the graph's axis, with units (or the class it stands for). */
  formatAxis(value: number): string
  /**
   * AYNI: the axis's own labels, short enough for the graph's margin (the
   * number alone); the units are then drawn once, at the top of the plot
   * (`axisUnits`). Without these the labels are `formatAxis`.
   */
  formatTick?(value: number): string
  axisUnits?(): string | null
  /** True when the series run in dates; false for playback time. */
  dated(): boolean
  /**
   * AYNI: a time on the graph as the date track names it: the frame a
   * click there seeks to, at the stream's precision ("Aug 2003" on a
   * monthly stream, "2075" on a yearly one). Null (or absent): the
   * graph's own span-based form.
   */
  frameLabel?(ms: number): string | null
  /** AYNI: the same frame for the CSV's time column, ISO 8601 cut to that precision ("2003-08"). */
  frameIso?(ms: number): string | null
  setPicking(on: boolean): void
  /** Drop pins on this frame's highest and lowest values; how many landed. */
  markExtremes(): number
  /**
   * AYNI (owner, 2026-10-18): whether the highest and lowest pins can follow
   * the data here (not on classes, which have no highest). Absent: they can.
   */
  canFollow?(): boolean
  /**
   * AYNI (owner, 2026-10-18): where the highest and lowest are looked for
   * (`measure/surfaceMask.ts`), offered once the land and sea tiles exist.
   * `surfaceStatus`: its map being made, made, none to be had, or none and
   * so everywhere instead ("fallback"). `markPending`: a press of Mark
   * highest & lowest waits for that map.
   */
  surfaceAvailable?(): boolean
  setSurface?(mode: SurfaceMode): void
  surfaceStatus?(): 'all' | 'loading' | 'ready' | 'none' | 'fallback'
  markPending?(): boolean
  announce?(message: string): void
  /** AYNI: the dataset's title and the values' units, for the CSV's header (pinsCsv.ts). */
  datasetTitle?(): string | null
  /**
   * AYNI: the pins as they are shown, e.g. as change from a held baseline
   * (`research/changeView.ts`); the store keeps what was read.
   */
  viewPins?(pins: readonly Pin[]): readonly Pin[]
  /** AYNI: what the CSV's values are, when they are not the dataset's own (a change from a baseline). */
  valuesNote?(): string | null
  /** AYNI: while a period baseline is shown, its mean and σ at a place for the frame at an instant (for the CSV); null otherwise. */
  pinReference?(): ((lat: number, lon: number, timeMs: number) => { mean: number | null; sigma: number | null } | null) | null
  /** AYNI: one quantisation step of the values shown (noise is never taken below it); null when unknown. */
  quantum?(): number | null
  /** AYNI: the dataset describes itself as a multi-model mean or median (its noise is too small for emergence). */
  ensemble?(): boolean
  /** The CSV's provenance rows: dataset id, link, credit, sources (`research/citation.ts`). */
  provenance?(): readonly (readonly (string | number | null)[])[] | null
  units?(): string | undefined
  /** AYNI: where playback is now, in the graph's time (the series' units); null when nothing plays. */
  now?(): number | null
  /** AYNI: move playback to this time on the graph's axis. */
  seek?(timeMs: number): void
  /**
   * AYNI (owner, 2026-10-09): the pointer over a graph, with each pin's
   * value at that time as the graph holds it (`value` null where that
   * frame held no data, `sampled` false where it was not read yet); null
   * once it leaves. The globe's pins show these while it is paused.
   */
  scrub?(at: PinScrub | null): void
  /** AYNI: open the plans, from the 'plan' state's button. */
  openPlans?(trigger: HTMLElement | null, reason?: 'data' | 'pins' | 'csv'): void
  /** True when the plan has no CSV downloads (Plus); the button then opens the plans. */
  csvLocked?(): boolean
  /**
   * AYNI (owner, 2026-10-14): the dataset's reference lines
   * (`referenceLines.ts`), dashed across the graph with a switch under it;
   * none while the values shown are not the dataset's own (a change).
   */
  referenceLines?(): readonly ReferenceLine[]
  /** AYNI (multi-globe plan): the same pins read on the other globes, for the graph and the CSV. */
  globeTraces?(): readonly GlobeTrace[]
  /**
   * AYNI: Trend, Emergence and Correlation are research tools (decision R2):
   * `open` on the plans that have them, `locked` (shown, opening the plans)
   * elsewhere, `hidden` for a class borrowing the data tools. Open when absent.
   */
  statistics?(): 'open' | 'locked' | 'hidden'
  /** AYNI: open the research plans, from a locked statistics button. */
  openResearchPlans?(trigger: HTMLElement): void
  /**
   * AYNI: where a graph taken out onto the globe goes back to. Without
   * it the graph stays in the panel.
   */
  dock?: {
    /** The Tools panel, while it is open. */
    panel(): HTMLElement | null
    /** The button that opens it. */
    button(): HTMLElement | null
    /** Open the panel on the Data tab. */
    showData(): void
    /** Close the panel where it covers the globe (a phone), as the graph comes out. */
    stepAside?(): void
  }
}

/**
 * AYNI (multi-globe plan, phase 1): a pin read on another globe — that
 * globe's dataset and date, on the primary's dates (the graph's x-axis),
 * drawn dashed in the pin's colour and named "Lima · globe 2 (−30 yr)".
 */
export interface GlobeTrace {
  readonly pinId: string
  /** The globe's number as people see it (2, 3, 4). */
  readonly globe: number
  /** Its time rule, as its chip prints it. */
  readonly ruleLabel: string
  readonly datasetTitle: string
  readonly series: MeasurementSeries
  /** Its latest reading there, formatted; '' with none. */
  readonly value: string
}

export interface DataPinsHandle {
  /** Re-check the dataset's readability (a dataset loaded, or the tab shown). */
  refresh(): void
  /** AYNI: turn pin mode on or off from elsewhere (the headset's Data page). On is refused while pins cannot be read or four are down. */
  setPicking(on: boolean): void
  isPicking(): boolean
  /** AYNI: the pins and the statistics shown under the graph, for the methods paragraph (research tier, §Next S5). */
  methodsState(): PinsMethodsState
  /** AYNI: the pins' CSV and their statistics alone, for the reproducibility bundle (§Next S8); only those that exist. */
  bundleCsvs(): BundleCsv[]
  /**
   * AYNI (the chatbot's `stats.*`, 2026-10-07): the pins as the panel shows
   * them (values, or their change from a held baseline), whether they run
   * in dates, and the emergence settings the panel uses now, so the
   * chatbot's numbers are the panel's.
   */
  statsView(): { pins: readonly Pin[]; dated: boolean; emergence: EmergenceView; units: string | undefined }
  /** AYNI (owner, 2026-10-22): whether the pin graph is on screen, in the panel or out over the globe (a room's lead says so). */
  graphShown(): boolean
  /**
   * AYNI (owner, 2026-10-22): a room's follower shows the graph when its lead
   * does: out over the globe, unless it is already on screen in the panel;
   * `false` puts a graph that is out back.
   */
  showGraph(on: boolean): void
}

/** AYNI: what the methods paragraph says of the pins: where they are, how much was read, which tests gave results. */
export interface PinsMethodsState {
  /** Pins with a series, named in English as the paragraph is. */
  readonly pins: readonly { label: string; lat: number; lon: number; read: number; slots: number }[]
  readonly trend: { pins: number; unit: 'decade' | 'year' | 'day' | 'hour' } | null
  readonly emergence: { pins: number; startYear: number; endYear: number; noise: number | null; threshold: number; windowYears: number; ensemble: boolean } | null
  readonly correlation: { pairs: number } | null
}

/** AYNI: the time under the pointer on a pin graph, and each pin's value there (`DataPinsDeps.scrub`). */
export interface PinScrub {
  readonly timeMs: number
  readonly pins: ReadonlyArray<{ readonly id: string; readonly value: number | null; readonly sampled: boolean }>
}

/** AYNI: a pin's name in English (the figure and the methods paragraph are, like the citation). */
export { pinEnglishName }

/** Unread slots a trace may cross: the frames a 200 ms sampler can miss at playback speed. */
const BRIDGE_SLOTS = 6

/** AYNI: points across a graph, about one per pixel of its plot (a series keeps every frame; the graph thins it). */
function chartColumns(g: ChartBox): number {
  return Math.max(2, Math.round(g.w - g.left - g.right))
}

/** Where the parts of a graph go, in its own units. */
interface ChartBox { w: number; h: number; left: number; right: number; top: number; plot: number; railTop: number; rail: number; railGap: number; big: boolean }

// Close to the width the panel shows it at, so 9-unit ticks read as ~9 px
// (AYNI: the Tools panel is 420 px wide since 2026-10-06).
const PANEL_CHART: ChartBox = { w: 390, h: 178, left: 44, right: 4, top: 8, plot: 130, railTop: 146, rail: 3, railGap: 2, big: false }

/** The graph in a window over the globe: in real pixels, with a grid and room for larger ticks. */
function windowChart(w: number, h: number, traces: number): ChartBox {
  const top = 10
  const rail = 4
  const railGap = 3
  const plot = Math.max(40, h - top - 10 - traces * (rail + railGap) - 20)
  return { w: Math.max(120, w), h, left: 64, right: 14, top, plot, railTop: top + plot + 10, rail, railGap, big: true }
}

/** AYNI: where a graph is drawn — in the Tools panel, or in its window over the globe. */
type Surface = 'panel' | 'window'

/** How far a press on the graph moves before it becomes a drag. */
const LIFT_MOVE_PX = 6

const ICON_RENAME = '<svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true"><path d="M3 13l.7-3L10.5 3.2l2.3 2.3L6 12.3zM9.3 4.4l2.3 2.3" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>'

const ICON_POP_OUT = '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path d="M9 3h4v4M13 3L7.5 8.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M11 9.5V13H3V5h3.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'

/** What the line under the surface choice says: its map coming, none to be had, or whose map it is. */
function surfaceNoteText(status: 'all' | 'loading' | 'ready' | 'none' | 'fallback'): string {
  if (status === 'loading') return t('tools.data.pins.surface.loading')
  if (status === 'none' || status === 'fallback') return t('tools.data.pins.surface.none')
  if (status === 'ready') return t('tools.data.pins.surface.credit')
  return ''
}

function coords(lat: number, lon: number): string {
  const ns = lat >= 0 ? 'N' : 'S'
  const ew = lon >= 0 ? 'E' : 'W'
  return `${Math.abs(lat).toFixed(1)}° ${ns}, ${Math.abs(lon).toFixed(1)}° ${ew}`
}

function axisTime(ms: number, spanMs: number, dated: boolean): string {
  if (!dated) {
    const s = Math.round(ms / 1000)
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
  }
  const day = 86_400_000
  // AYNI: in UTC, as the date track and the readout: the series' times
  // are the stream's UTC stamps, and a device's timezone moved a stamp at
  // midnight on the 1st into the month before.
  if (spanMs > 20 * 365 * day) return formatDate(ms, { year: 'numeric', timeZone: 'UTC' })
  if (spanMs <= 2 * day) return formatDate(ms, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
  if (spanMs <= 400 * day) return formatDate(ms, { month: 'short', day: 'numeric', timeZone: 'UTC' })
  return formatDate(ms, { year: 'numeric', month: 'short', timeZone: 'UTC' })
}

function pathFor(points: ReadonlyArray<{ x: number; y: number }>, g: ChartBox): string {
  const plotW = g.w - g.left - g.right
  return points
    .map((p, i) => `${i ? 'L' : 'M'}${(g.left + p.x * plotW).toFixed(1)},${(g.top + p.y * g.plot).toFixed(1)}`)
    .join('')
}

export function mountDataPins(host: HTMLElement, deps: DataPinsDeps): DataPinsHandle {
  const { store } = deps
  let picking = false
  let signature = ''

  function setPicking(on: boolean): void {
    if (picking === on) return
    picking = on
    deps.setPicking(on)
  }

  /** AYNI: the pins as shown (values, or their change from a held baseline). */
  function shownPins(): readonly Pin[] {
    return deps.viewPins ? deps.viewPins(store.list()) : store.list()
  }

  function readable(r: PinReadability): boolean {
    return r === 'release' || r === 'encoded'
  }

  function rowValue(pin: Pin): string {
    if (!readable(deps.readability())) return ''
    if (!pin.reading) return t('tools.data.pins.nothing')
    return deps.format(pin.reading)
  }

  // --- AYNI: the playhead, the hover line and click-to-seek ----------------

  /** What each surface last drew, so a pointer move can redraw its hover line alone. */
  const drawn: Partial<Record<Surface, { chart: ChartGeometry; pins: readonly Pin[]; g: ChartBox }>> = {}
  /** Where the pointer is over a graph, as a fraction of its time axis. */
  let hover: { surface: Surface; frac: number } | null = null

  /**
   * AYNI: a pin's series as its line is drawn (thinned to the graph's
   * columns), so the hover's values are the line's, not one frame among
   * the many a column holds.
   */
  function drawnSeries(chart: ChartGeometry, pin: Pin): MeasurementSeries | null {
    return chart.traces.find((tr) => tr.id === pin.id)?.series ?? pin.series
  }

  /** The time a fraction of the axis stands for. */
  function timeAt(series: { startMs: number; endMs: number }, frac: number): number {
    return series.startMs + frac * (series.endMs - series.startMs)
  }

  /** AYNI: a point on the axis named as the frame a click there seeks to, else by the span. */
  function pointTime(ms: number, series: { startMs: number; endMs: number }): string {
    const dated = deps.dated()
    return (dated ? deps.frameLabel?.(ms) : null) ?? axisTime(ms, series.endMs - series.startMs, dated)
  }

  /** The green line at the time playback is at, if that is on this graph's axis. */
  function playheadMarkup(series: { startMs: number; endMs: number }, g: ChartBox): string {
    const now = deps.now?.() ?? null
    const span = series.endMs - series.startMs
    if (now === null || !(span > 0)) return ''
    const frac = (now - series.startMs) / span
    if (frac < 0 || frac > 1) return ''
    const x = (g.left + frac * (g.w - g.left - g.right)).toFixed(1)
    return `<line x1="${x}" y1="${g.top}" x2="${x}" y2="${g.top + g.plot}" class="tools-data-playhead"/>`
  }

  /**
   * The dashed line under the pointer, a dot where it crosses each trace,
   * and the time there; in the window also a box with each pin's value.
   */
  function hoverMarkup(surface: Surface, frac: number): string {
    const d = drawn[surface]
    const series = d?.pins.find((p) => p.series)?.series
    if (!d || !series) return ''
    const { chart, g } = d
    const plotW = g.w - g.left - g.right
    const x = g.left + frac * plotW
    const bottom = g.top + g.plot
    const range = chart.max - chart.min
    const rows = d.pins.map((pin, i) => {
      const slots = drawnSeries(d.chart, pin)?.slots ?? []
      const slot = slots[Math.round(frac * Math.max(1, slots.length - 1))]
      return { pin, i, sampled: !!slot?.sampled, value: slot?.sampled ? slot.value : null }
    })
    const dots = rows.filter((r) => r.value !== null).map((r) => {
      const y = g.top + (range > 0 ? 1 - (r.value! - chart.min) / range : 0.5) * g.plot
      return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${g.big ? 4 : 2.6}" fill="${escapeAttr(r.pin.color)}" class="tools-data-hover-dot"/>`
    }).join('')
    const time = pointTime(timeAt(series, frac), series)
    const plotR = g.w - g.right
    // AYNI (owner, 2026-10-09): the time and values right of the line,
    // the hint left of it; a side swaps only where it would run off the plot.
    let label: string
    let right: boolean
    /** How far down the time and values reach, on their side. */
    let valuesBottom: number
    if (!g.big) {
      right = x + 3 + time.length * 5.4 <= plotR
      label = `<text x="${(x + (right ? 3 : -3)).toFixed(1)}" y="${g.top + 7}" text-anchor="${right ? 'start' : 'end'}" class="tools-data-tick tools-data-hover-time">${escapeHtml(time)}</text>`
      valuesBottom = g.top + 9
    } else {
      const lines = [time, ...rows.map((r) => `${pinName(r.pin, r.i)}  ${r.value !== null ? deps.formatAxis(r.value) : r.sampled ? t('tools.data.pins.nothing') : t('tools.data.graph.notRead')}`)]
      const lineH = 15
      const boxW = Math.min(plotW, 14 + 6.6 * Math.max(...lines.map((l) => l.length)))
      const boxH = 8 + lines.length * lineH
      right = x + 8 + boxW <= plotR
      const bx = right ? x + 8 : x - 8 - boxW
      const by = g.top + 4
      valuesBottom = by + boxH
      label = `
        <rect x="${bx.toFixed(1)}" y="${by}" width="${boxW.toFixed(1)}" height="${boxH}" rx="5" class="tools-data-hover-box"/>
        ${lines.map((l, k) => `
        ${k > 0 ? `<circle cx="${(bx + 9).toFixed(1)}" cy="${by + 4 + k * lineH + 7}" r="3.2" fill="${escapeAttr(rows[k - 1].pin.color)}"/>` : ''}
        <text x="${(bx + (k > 0 ? 17 : 8)).toFixed(1)}" y="${by + 4 + k * lineH + 11}" class="tools-data-hover-text${k === 0 ? ' tools-data-hover-time' : ''}">${escapeHtml(l)}</text>`).join('')}`
    }
    return `
      <g class="tools-data-hover" pointer-events="none">
        <line x1="${x.toFixed(1)}" y1="${g.top}" x2="${x.toFixed(1)}" y2="${bottom}" class="tools-data-hover-line"/>
        ${dots}
        ${seekHintMarkup(g, x, right, valuesBottom)}
        ${label}
      </g>`
  }

  /**
   * AYNI: "Click to play from here", on the other side of the line from
   * the time and values and level with the time. Where it does not fit
   * there (the line near that edge) it goes on their side, under them.
   */
  function seekHintMarkup(g: ChartBox, x: number, valuesRight: boolean, valuesBottom: number): string {
    if (!deps.seek) return ''
    const hint = t('tools.data.graph.seekHint')
    const width = hint.length * (g.big ? 6 : 5)
    const gap = g.big ? 8 : 4
    const fits = valuesRight ? x - gap - width >= g.left : x + gap + width <= g.w - g.right
    const onRight = fits ? !valuesRight : valuesRight
    const y = fits
      ? (g.big ? g.top + 19 : g.top + 7)
      : Math.min(g.top + g.plot - 3, valuesBottom + (g.big ? 14 : 10))
    return `<text x="${(onRight ? x + gap : x - gap).toFixed(1)}" y="${y}" text-anchor="${onRight ? 'start' : 'end'}" class="tools-data-hover-hint${g.big ? ' big' : ''}">${escapeHtml(hint)}</text>`
  }

  /** AYNI: each pin's value at a point on a surface's axis, as the hover box reads it. */
  function scrubAt(surface: Surface, frac: number): PinScrub | null {
    const d = drawn[surface]
    const series = d?.pins.find((p) => p.series)?.series
    if (!d || !series) return null
    return {
      timeMs: timeAt(series, frac),
      pins: d.pins.map((pin) => {
        const slots = drawnSeries(d.chart, pin)?.slots ?? []
        const slot = slots[Math.round(frac * Math.max(1, slots.length - 1))]
        return { id: pin.id, value: slot?.sampled ? slot.value : null, sampled: !!slot?.sampled }
      }),
    }
  }

  /** Redraw one surface's hover line in place: the pointer moves far more often than the graph is rebuilt. */
  function paintHover(container: HTMLElement, surface: Surface): void {
    const svg = container.querySelector('svg.tools-data-chart-svg')
    if (!svg) return
    svg.querySelector('.tools-data-hover')?.remove()
    if (hover?.surface === surface) svg.insertAdjacentHTML('beforeend', hoverMarkup(surface, hover.frac))
  }

  /** Where on the time axis a pointer is, as 0–1, or null when it is off the plot. */
  function fracAt(container: HTMLElement, surface: Surface, clientX: number): number | null {
    const svg = container.querySelector<SVGSVGElement>('svg.tools-data-chart-svg')
    const d = drawn[surface]
    if (!svg || !d) return null
    // Through the SVG's own matrix, which knows about a letterboxed viewBox;
    // by its box where there is none (a test DOM).
    const ctm = typeof svg.getScreenCTM === 'function' ? svg.getScreenCTM() : null
    let x: number
    if (ctm && typeof DOMPoint === 'function') {
      x = new DOMPoint(clientX, 0).matrixTransform(ctm.inverse()).x
    } else {
      const r = svg.getBoundingClientRect()
      if (!(r.width > 0)) return null
      x = ((clientX - r.left) / r.width) * d.g.w
    }
    const plotW = d.g.w - d.g.left - d.g.right
    const frac = (x - d.g.left) / plotW
    // A little slack past each end, so the first and last frame are easy to hit.
    if (frac < -0.03 || frac > 1.03) return null
    return Math.min(1, Math.max(0, frac))
  }

  const scrubBound = new WeakSet<HTMLElement>()
  /** Hover and click-to-seek on a graph's container; wired once, it outlives every rebuild inside it. */
  function bindScrub(container: HTMLElement, surface: Surface): void {
    if (scrubBound.has(container) || !deps.seek) return
    scrubBound.add(container)
    container.addEventListener('pointermove', (e) => {
      const frac = fracAt(container, surface, e.clientX)
      hover = frac === null ? null : { surface, frac }
      paintHover(container, surface)
      deps.scrub?.(frac === null ? null : scrubAt(surface, frac))
    })
    container.addEventListener('pointerleave', () => {
      if (hover?.surface !== surface) return
      hover = null
      paintHover(container, surface)
      deps.scrub?.(null)
    })
    container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('button')) return
      const frac = fracAt(container, surface, e.clientX)
      const series = drawn[surface]?.pins.find((p) => p.series)?.series
      if (frac === null || !series) return
      e.stopPropagation()
      const ms = timeAt(series, frac)
      deps.seek?.(ms)
      // The line goes there at once; the next reading confirms it.
      const x = (drawn[surface]!.g.left + frac * (drawn[surface]!.g.w - drawn[surface]!.g.left - drawn[surface]!.g.right)).toFixed(1)
      container.querySelectorAll('.tools-data-playhead').forEach((line) => {
        line.setAttribute('x1', x)
        line.setAttribute('x2', x)
      })
      deps.announce?.(t('tools.data.graph.seeked', { time: pointTime(ms, series) }))
    })
  }

  // --- AYNI (owner, 2026-10-14): reference lines ----------------------------

  /** The dataset's lines, shown or not. */
  function allRefs(): readonly ReferenceLine[] {
    return deps.referenceLines?.() ?? []
  }

  /** The lines the graph draws now: none while the switch hides them. */
  function shownRefs(): readonly ReferenceLine[] {
    return referenceLinesVisible() ? allRefs() : []
  }

  /** Each line dashed across the plot, its short name at the right end, above it (below, when another sits just over it). */
  function refsSvg(chart: ChartGeometry, g: ChartBox): string {
    const refs = shownRefs()
    if (!refs.length || refs.length !== chart.references.length) return ''
    const placed = refs
      .map((line, i) => ({ line, y: chart.references[i] }))
      .filter((r): r is { line: ReferenceLine; y: number } => r.y !== null)
      .map((r) => ({ line: r.line, py: g.top + r.y * g.plot }))
      .sort((a, b) => a.py - b.py)
    const gapPx = g.big ? 14 : 11
    return placed.map((r, i) => {
      const crowded = i > 0 && r.py - placed[i - 1].py < gapPx
      const below = crowded || r.py - g.top < gapPx
      const ty = below ? r.py + (g.big ? 12 : 9) : r.py - 3
      return `
        <line x1="${g.left}" y1="${r.py.toFixed(1)}" x2="${g.w - g.right}" y2="${r.py.toFixed(1)}" class="tools-data-ref${g.big ? ' big' : ''}"/>
        <text x="${g.w - g.right - 2}" y="${ty.toFixed(1)}" text-anchor="end" class="tools-data-tick tools-data-ref-label">${escapeHtml(referenceShort(r.line))}</text>`
    }).join('')
  }

  /** Under the graph: the switch, then each line named in full (and where it is, when off the graph). */
  function refsKeyHtml(chart: ChartGeometry): string {
    const refs = allRefs()
    if (!refs.length) return ''
    const on = referenceLinesVisible()
    const keys = !on ? '' : refs.map((line, i) => {
      const y = chart.references[i]
      const off = y !== null && y !== undefined ? '' : line.value > chart.max ? t('reference.offAbove') : t('reference.offBelow')
      return `
        <span class="tools-data-ref-key" title="${escapeAttr(referenceNote(line))}">
          <span class="tools-data-ref-swatch" aria-hidden="true"></span>
          <span class="tools-data-ref-name">${escapeHtml(referenceLabel(line))}</span>${off ? ` <span class="tools-data-ref-off">${escapeHtml(off)}</span>` : ''}
        </span>`
    }).join('')
    return `
      <button type="button" class="tools-data-ref-toggle${on ? ' active' : ''}" data-ref-toggle aria-pressed="${on}">
        <span class="tools-menu-item-check" aria-hidden="true"></span>
        <span>${tHtml('reference.toggle')}</span>
      </button>${keys}`
  }

  function onRefsClick(ev: MouseEvent): void {
    const btn = (ev.target as HTMLElement | null)?.closest('[data-ref-toggle]')
    if (!btn) return
    ev.stopPropagation()
    const box = ev.currentTarget as HTMLElement | null
    const on = !referenceLinesVisible()
    setReferenceLinesVisible(on)
    // The key is drawn afresh; the focus stays on the switch.
    box?.querySelector<HTMLElement>('[data-ref-toggle]')?.focus()
    deps.announce?.(t(on ? 'reference.shown' : 'reference.hidden'))
  }

  function chartSvg(chart: ChartGeometry, pins: readonly Pin[], g: ChartBox = PANEL_CHART, surface: Surface = 'panel'): string {
    const series = pins.find((p) => p.series)?.series
    if (!series) return ''
    drawn[surface] = { chart, pins, g }
    const span = series.endMs - series.startMs
    const dated = deps.dated()
    const plotW = g.w - g.left - g.right
    const railW = plotW / chart.slots
    const stroke = g.big ? 2.2 : 1.6
    const dot = g.big ? 2.6 : 1.8
    // AYNI (multi-globe plan): another globe's reading of a pin is dashed, in the pin's colour.
    const traces = chart.traces.map((tr) => {
      const other = tr.id.startsWith('globe:')
      const dash = other ? ` stroke-dasharray="${g.big ? '5 4' : '3 3'}" stroke-opacity="0.85"` : ''
      return `
      ${tr.segments.map((seg) => `<path d="${pathFor(seg, g)}" fill="none" stroke="${escapeAttr(tr.color)}" stroke-width="${stroke}" stroke-linejoin="round"${dash}/>`).join('')}
      ${tr.dots.map((d) => `<circle cx="${(g.left + d.x * plotW).toFixed(1)}" cy="${(g.top + d.y * g.plot).toFixed(1)}" r="${dot}" fill="${escapeAttr(tr.color)}"${other ? ' fill-opacity="0.7"' : ''}/>`).join('')}`
    }).join('')
    const rails = chart.traces.map((tr, i) => {
      const y = g.railTop + i * (g.rail + g.railGap)
      return tr.rail.map((state, s) => state === 'unsampled' ? '' : `<rect x="${(g.left + s * railW).toFixed(1)}" y="${y}" width="${Math.max(0.6, railW).toFixed(2)}" height="${g.rail}" fill="${escapeAttr(tr.color)}"${state === 'nodata' ? ' fill-opacity="0.3"' : ''}/>`).join('')
    }).join('')
    const bottom = g.top + g.plot
    const labelY = g.railTop + chart.traces.length * (g.rail + g.railGap) + (g.big ? 13 : 9)
    const tickX = g.left - (g.big ? 6 : 4)
    const tick = (v: number) => (deps.formatTick ?? deps.formatAxis)(v)
    const units = deps.formatTick ? deps.axisUnits?.() ?? null : null
    // A window has room for a grid at the quarters, and the middle value and time.
    const extra = !g.big ? '' : `
        ${[0.25, 0.5, 0.75].map((f) => `<line x1="${g.left}" y1="${(g.top + f * g.plot).toFixed(1)}" x2="${g.w - g.right}" y2="${(g.top + f * g.plot).toFixed(1)}" class="tools-data-grid"/>`).join('')}
        <text x="${tickX}" y="${(g.top + g.plot / 2 + 4).toFixed(1)}" text-anchor="end" class="tools-data-tick">${escapeHtml(tick((chart.min + chart.max) / 2))}</text>
        <text x="${(g.left + plotW / 2).toFixed(1)}" y="${labelY}" text-anchor="middle" class="tools-data-tick">${escapeHtml(axisTime((series.startMs + series.endMs) / 2, span, dated))}</text>`
    const height = Math.max(g.h, labelY + 2)
    const label = t('tools.data.chart.aria', {
      count: chart.traces.length,
      start: axisTime(series.startMs, span, dated),
      end: axisTime(series.endMs, span, dated),
      min: deps.formatAxis(chart.min),
      max: deps.formatAxis(chart.max),
    })
    return `
      <svg class="tools-data-chart-svg" viewBox="0 0 ${g.w} ${height}"${g.big ? ` width="${g.w}" height="${height}"` : ''} role="img" aria-label="${escapeAttr(label)}">
        ${extra}
        <line x1="${g.left}" y1="${g.top}" x2="${g.left}" y2="${bottom}" class="tools-data-axis"/>
        <line x1="${g.left}" y1="${bottom}" x2="${g.w - g.right}" y2="${bottom}" class="tools-data-axis"/>
        <text x="${tickX}" y="${g.top + 4}" text-anchor="end" class="tools-data-tick">${escapeHtml(tick(chart.max))}</text>
        <text x="${tickX}" y="${bottom}" text-anchor="end" class="tools-data-tick">${escapeHtml(tick(chart.min))}</text>
        ${units ? `<text x="${g.left + 3}" y="${g.top + 4}" class="tools-data-tick tools-data-units">${escapeHtml(units)}</text>` : ''}
        ${refsSvg(chart, g)}
        ${traces}
        ${emergenceOn && dated && statsOpen() ? emergenceSvg(pins.filter((p) => p.series), { left: g.left, top: g.top, plotW, plot: g.plot, min: chart.min, max: chart.max, startMs: series.startMs, endMs: series.endMs }, emergenceView(pins)) : ''}
        ${trendOn && dated && statsOpen() ? trendLinesSvg(pins.filter((p) => p.series), { left: g.left, top: g.top, plotW, plot: g.plot, min: chart.min, max: chart.max, startMs: series.startMs, endMs: series.endMs }, g.big) : ''}
        ${rails}
        ${playheadMarkup(series, g)}
        ${hover?.surface === surface ? hoverMarkup(surface, hover.frac) : ''}
        <text x="${g.left}" y="${labelY}" class="tools-data-tick">${escapeHtml(axisTime(series.startMs, span, dated))}</text>
        <text x="${g.w - g.right}" y="${labelY}" text-anchor="end" class="tools-data-tick">${escapeHtml(axisTime(series.endMs, span, dated))}</text>
      </svg>`
  }

  // --- AYNI: the graph out on the globe -----------------------------------

  /** The window, while the graph is out of the panel. */
  let graphWin: PinGraphWindow | null = null
  /** AYNI: statistics on the graph and under it (research tier, pinTrendUI.ts). */
  let trendOn = false
  let emergenceOn = false
  let correlationOn = false
  /** The emergence baseline period (null: the default for the series) and noise brought by the reader. */
  let emergencePeriod: YearPeriod | null = null
  let emergenceNoise: number | null = null
  /** The last results drawn per section, so an unchanged one is not redrawn (nor re-read by a screen reader). */
  const lastSection = new Map<string, string>()

  function emergenceView(pins: readonly Pin[]): EmergenceView {
    const first = pins.find((p) => p.series)
    return {
      period: emergencePeriod ?? (first ? defaultYearPeriod(first) : null) ?? { startYear: 1950, endYear: 1979 },
      noise: emergenceNoise,
      quantum: deps.quantum?.() ?? null,
      ensemble: deps.ensemble?.() ?? false,
    }
  }

  /** The series' years, for the period inputs. */
  function yearBounds(pins: readonly Pin[]): { minYear: number; maxYear: number } {
    const s = pins.find((p) => p.series)?.series
    return s ? { minYear: new Date(s.startMs).getUTCFullYear(), maxYear: new Date(s.endMs).getUTCFullYear() } : { minYear: 1900, maxYear: 2100 }
  }

  /** Put a section's HTML in its container only when it changed. */
  function setSection(id: string, html: string): void {
    const el = host.querySelector<HTMLElement>(`#${id}`)
    if (!el || lastSection.get(id) === html) return
    lastSection.set(id, html)
    el.innerHTML = html
  }
  /** True from the moment it is lifted out until it lands or goes back. */
  let carrying = false
  /** The graph's height in the panel when it left, kept for its place. */
  let leftHeight = 0

  /** AYNI (multi-globe plan): the other globes' series, each in its pin's colour, as traces named `globe:<n>:<pin>`. */
  function globeTraces(pins: readonly Pin[]): Array<{ trace: GlobeTrace; pin: Pin; i: number }> {
    const out: Array<{ trace: GlobeTrace; pin: Pin; i: number }> = []
    for (const trace of deps.globeTraces?.() ?? []) {
      const i = pins.findIndex((p) => p.id === trace.pinId)
      if (i >= 0) out.push({ trace, pin: pins[i], i })
    }
    return out
  }

  function globeTraceLabel(g: { trace: GlobeTrace; pin: Pin; i: number }): string {
    return t('tools.data.graph.globeKey', { name: pinName(g.pin, g.i), n: g.trace.globe, rule: g.trace.ruleLabel })
  }

  function timedPins(g: ChartBox = PANEL_CHART): { pins: readonly Pin[]; timed: Pin[]; chart: ChartGeometry | null } {
    const pins = shownPins()
    const timed = pins.filter((p) => p.series)
    // The other globes' traces draw after the pins' own, dashed (`chartSvg`).
    const others = globeTraces(pins).map((g) => ({ id: `globe:${g.trace.globe}:${g.pin.id}`, color: g.pin.color, series: g.trace.series }))
    const chart = buildSeriesChart([...timed.map((p) => ({ id: p.id, color: p.color, series: p.series! })), ...others], { bridge: BRIDGE_SLOTS, columns: chartColumns(g), references: shownRefs().map((r) => r.value) })
    return { pins, timed, chart }
  }

  /** The window's contents: the graph at its size, and a legend with each pin's latest value. */
  function drawWindow(body: HTMLElement, foot: HTMLElement, width: number, height: number): void {
    const { pins, timed, chart } = timedPins(windowChart(width, height, 0))
    body.innerHTML = chart
      ? chartSvg(chart, timed, windowChart(width, height, chart.traces.length), 'window')
      : `<p class="pin-graph-empty">${escapeHtml(chartNote(pins, timed, chart) || t('tools.data.graph.noPins'))}</p>`
    const note = chart ? chartNote(pins, timed, chart) : ''
    bindScrub(body, 'window')
    foot.innerHTML = `${pins.map((pin, i) => `
      <span class="pin-graph-key">
        <span class="tools-data-pin-swatch" style="--pin-color:${escapeAttr(pin.color)}" aria-hidden="true"></span>
        <span class="pin-graph-key-name">${escapeHtml(pinName(pin, i))}</span>
        <span class="pin-graph-key-value">${escapeHtml(rowValue(pin))}</span>
      </span>`).join('')}${globeTraces(pins).map((gt) => `
      <span class="pin-graph-key is-globe">
        <span class="tools-data-pin-swatch is-dashed" style="--pin-color:${escapeAttr(gt.pin.color)}" aria-hidden="true"></span>
        <span class="pin-graph-key-name">${escapeHtml(globeTraceLabel(gt))}</span>
        <span class="pin-graph-key-value">${escapeHtml(gt.trace.value)}</span>
      </span>`).join('')}${chart ? refsKeyHtml(chart) : ''}${note ? `<span class="pin-graph-note">${escapeHtml(note)}</span>` : ''}`
    foot.onclick = onRefsClick
  }

  function dockTargets(): HTMLElement[] {
    return [deps.dock?.panel(), deps.dock?.button()].filter((el): el is HTMLElement => !!el && el.getBoundingClientRect().width > 0)
  }

  /** Where it flies back to: its place in the panel when that shows, else the Tools button. */
  function dockRect(reason: DockReason, target: HTMLElement | null): DOMRect | null {
    const panel = deps.dock?.panel() ?? null
    if (reason === 'button' || (target && target === panel)) deps.dock?.showData()
    const place = host.querySelector<HTMLElement>('#tools-data-chart-away')
    const r = place?.getBoundingClientRect()
    if (r && r.width > 0 && r.height > 0) return r
    return deps.dock?.button()?.getBoundingClientRect() ?? null
  }

  function takeOut(): PinGraphWindow {
    const chartBox = host.querySelector<HTMLElement>('#tools-data-chart')
    leftHeight = chartBox?.getBoundingClientRect().height ?? 0
    const win = createPinGraphWindow({
      draw: drawWindow,
      download: downloadPins,
      dockTargets,
      dockRect,
      onLanded: () => {
        carrying = false
        updateValues()
        deps.announce?.(t('tools.data.graph.outAnnounce'))
      },
      onDocked: (reason) => {
        graphWin = null
        if (hover?.surface === 'window') {
          hover = null
          deps.scrub?.(null)
        }
        carrying = false
        updateValues()
        const back = host.querySelector<HTMLElement>('#tools-data-chart')
        if (back && !back.hidden) {
          back.classList.remove('landed')
          void back.offsetWidth
          back.classList.add('landed')
          setTimeout(() => back.classList.remove('landed'), 900)
        }
        if (reason === 'button') host.querySelector<HTMLElement>('#tools-data-chart-pop')?.focus()
        deps.announce?.(t('tools.data.graph.backAnnounce'))
      },
    })
    graphWin = win
    return win
  }

  /** The ⇱ button: out to where it last sat, with no drag. */
  function popOut(): void {
    if (skipClick) {
      skipClick = false
      return
    }
    if (graphWin || !deps.dock) return
    const from = host.querySelector<HTMLElement>('#tools-data-chart')?.getBoundingClientRect()
    if (!from) return
    const win = takeOut()
    updateValues()
    deps.dock.stepAside?.()
    win.popOut(from)
    deps.announce?.(t('tools.data.graph.outAnnounce'))
  }

  /** AYNI: the graph in the panel, when it is on screen there. */
  function panelChartRect(): DOMRect | null {
    const r = host.querySelector<HTMLElement>('#tools-data-chart')?.getBoundingClientRect()
    return r && r.width > 0 && r.height > 0 ? r : null
  }

  /**
   * AYNI (owner, 2026-10-22): out over the globe for a room's follower, from
   * the panel's graph when it shows, else from the Tools button, else from
   * the middle of the screen (an audience has neither).
   */
  function showGraphOut(): void {
    if (graphWin || !deps.dock || panelChartRect()) return
    const button = deps.dock.button()?.getBoundingClientRect()
    const from = button && button.width > 0 ? button : new DOMRect(window.innerWidth / 2, window.innerHeight / 2, 0, 0)
    const win = takeOut()
    updateValues()
    win.popOut(from)
    deps.announce?.(t('tools.data.graph.outAnnounce'))
  }

  /** Set when a press on ⇱ became a drag, so the click that follows does not pop it out again. */
  let skipClick = false

  /**
   * A press on the graph in the panel that moves a few pixels lifts it
   * out. A finger does it by ⇱ only: over the graph itself it scrolls the
   * panel, and once a browser has taken a touch for scrolling no page can
   * have it back, so ⇱ is `touch-action: none` and the graph is not.
   */
  function onChartPointerDown(e: PointerEvent): void {
    skipClick = false
    if (!deps.dock || graphWin || e.button > 0) return
    const onPop = !!(e.target as HTMLElement).closest('#tools-data-chart-pop')
    if (e.pointerType === 'touch' && !onPop) return
    const box = e.currentTarget as HTMLElement
    if (!onPop) e.preventDefault()
    const move = (m: PointerEvent) => {
      if (m.pointerId !== e.pointerId || Math.hypot(m.clientX - e.clientX, m.clientY - e.clientY) <= LIFT_MOVE_PX) return
      cleanup()
      skipClick = onPop
      const from = box.getBoundingClientRect()
      carrying = true
      const win = takeOut()
      updateValues()
      // On a phone the panel is most of the screen: it gets out of the way of the drop.
      deps.dock?.stepAside?.()
      win.carry(m, from)
    }
    const cleanup = () => {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', cleanup)
      document.removeEventListener('pointercancel', cleanup)
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', cleanup)
    document.addEventListener('pointercancel', cleanup)
  }

  /** AYNI: the pins' series as a CSV file (pinsCsv.ts), each pin named as in the list. */
  function downloadPins(trigger: HTMLElement | null = null): void {
    // AYNI (Plus, owner 2026-10-06): no CSV on the plan; the button opens the plans instead.
    if (deps.csvLocked?.()) {
      deps.openPlans?.(trigger, 'csv')
      return
    }
    const part = pinsCsv()
    if (!part) return
    downloadCsv(part.name, part.text)
    deps.announce?.(t('tools.data.csv.done'))
  }

  /** AYNI: the pins' CSV as the button saves it; null when no pin has a series. */
  function pinsCsv(): { name: string; text: string } | null {
    const pins = shownPins()
    const at = new Date()
    const title = deps.datasetTitle?.() ?? null
    // AYNI (multi-globe plan): the other globes' readings as rows of their own, with a note saying what they are.
    const others = globeTraces(pins)
    const globeNote = others.length
      ? t('tools.data.csv.globeNote', { datasets: [...new Set(others.map((g) => `${g.trace.globe}: ${g.trace.datasetTitle} (${g.trace.ruleLabel})`))].join('; ') })
      : null
    const valuesNote = [deps.valuesNote?.() ?? null, globeNote].filter((s): s is string => !!s).join(' ') || undefined
    const text = buildPinsCsvText(
      [
        ...pins.map((pin, i) => ({ name: pinName(pin, i), lat: pin.lat, lon: pin.lon, series: pin.series ?? null })),
        ...others.map((g) => ({ name: globeTraceLabel(g), lat: g.pin.lat, lon: g.pin.lon, series: g.trace.series })),
      ],
      {
        datasetTitle: title, units: deps.units?.(), dated: deps.dated(), exportedAt: at, provenance: deps.provenance?.() ?? undefined, valuesNote, formatTime: (ms) => deps.frameIso?.(ms) ?? null,
        trends: statsCsvRows(pins),
        // AYNI (owner, 2026-10-18): where the following pins looked, when not everywhere.
        surface: pins.some((p) => p.follow) && store.surface !== 'all' ? store.surface : undefined,
        reference: (() => {
          const ref = deps.pinReference?.() ?? null
          return ref ? (pin: { lat: number; lon: number }, timeMs: number) => ref(pin.lat, pin.lon, timeMs) : undefined
        })(),
      },
    )
    return text ? { name: pinsCsvFilename(title, at), text } : null
  }

  /** AYNI (S8): the trend, emergence and correlation tables alone, with the file's header; null when none is on. */
  function pinStatisticsCsv(): string | null {
    const rows = statsCsvRows(shownPins())
    if (!rows?.length) return null
    return buildPinStatsCsvText(rows, {
      datasetTitle: deps.datasetTitle?.() ?? null,
      units: deps.units?.(),
      exportedAt: new Date(),
      provenance: deps.provenance?.() ?? undefined,
      valuesNote: deps.valuesNote?.() ?? undefined,
    })
  }

  /** AYNI: the CSV's statistics blocks: trends, emergence and correlation, whichever are on, separated by a blank row. */
  /** AYNI: the statistics may be shown (a plan with the research tools). */
  function statsOpen(): boolean {
    return (deps.statistics?.() ?? 'open') === 'open'
  }

  function statsCsvRows(pins: readonly Pin[]): (string | number | null)[][] | undefined {
    if (!deps.dated() || !statsOpen()) return undefined
    const timed = pins.filter((p) => p.series)
    const names = timed.map((p) => pinName(p, pins.findIndex((q) => q.id === p.id)))
    const blocks = [
      trendOn ? trendCsvRows(timed, names) : null,
      emergenceOn ? emergenceCsvRows(timed, names, emergenceView(pins)) : null,
      correlationOn ? correlationCsvRows(timed, names) : null,
    ].filter((b): b is (string | number | null)[][] => !!b)
    if (!blocks.length) return undefined
    return blocks.flatMap((b, i) => (i ? [[], ...b] : b))
  }

  /** The line under the graph: why there is none, or how much of it is filled in. */
  function chartNote(pins: readonly Pin[], timed: readonly Pin[], chart: ChartGeometry | null): string {
    if (!pins.length) return ''
    if (!timed.length && pins.some((p) => p.reading)) return t('tools.data.chart.still')
    if (!chart) return t('tools.data.chart.empty')
    // AYNI: on the columns drawn, as the rail shows it (a long stream has far more frames than a lap reads).
    const own = new Set(timed.map((p) => p.id))
    const seen = Math.max(...chart.traces.filter((tr) => own.has(tr.id)).map((tr) => {
      const c = seriesCoverage(tr.series)
      return c.sampled / c.total
    }))
    return seen < 0.98 ? t('tools.data.chart.filling', { pct: formatNumber(Math.max(1, Math.round(seen * 100))) }) : ''
  }

  /**
   * AYNI (owner, 2026-10-18): when each pin's series was highest and lowest,
   * over the frames read: the value, the frame's date as a button that plays
   * from there, the other frames holding the same value, and, for a pin that
   * follows the extreme, where it was. Empty for classes (no highest).
   */
  function whenHtml(pins: readonly Pin[], timed: readonly Pin[]): string {
    if (!timed.length || !(deps.canFollow?.() ?? true)) return ''
    const dated = deps.dated()
    const end = (pin: Pin, kind: 'max' | 'min', e: SeriesWhen): string => {
      const time = pointTime(e.timeMs, pin.series!)
      const ties = e.others === 0 ? '' : ` <span class="tools-data-when-ties">${escapeHtml(t(e.others === 1 ? 'tools.data.when.tiesOne' : 'tools.data.when.ties', { count: formatNumber(e.others) }))}</span>`
      const where = e.at ? ` <span class="tools-data-when-where">${escapeHtml(t('tools.data.when.at', { where: coords(e.at.lat, e.at.lon) }))}</span>` : ''
      const go = deps.seek
        ? `<button type="button" class="tools-data-when-time" data-when-seek="${e.timeMs}" aria-label="${escapeAttr(t('tools.data.when.seek', { time }))}">${escapeHtml(time)}</button>`
        : `<span class="tools-data-when-time">${escapeHtml(time)}</span>`
      return `
          <span class="tools-data-when-row" data-when="${kind}">
            <span class="tools-data-when-label">${tHtml(kind === 'max' ? 'tools.data.when.highest' : 'tools.data.when.lowest')}</span>
            <span class="tools-data-when-value">${escapeHtml(deps.formatAxis(e.value))}</span>
            ${go}${ties}${where}
          </span>`
    }
    const rows = timed.map((pin) => {
      const e = seriesWhenExtremes(pin.series)
      if (!e) return ''
      const i = pins.findIndex((p) => p.id === pin.id)
      const part = e.read < e.total
        ? `<span class="tools-data-when-read">${escapeHtml(t('tools.data.when.partial', { read: formatNumber(e.read), total: formatNumber(e.total) }))}</span>`
        : ''
      return `
        <li class="tools-data-when-pin" data-pin="${escapeAttr(pin.id)}">
          <span class="tools-data-when-name"><span class="tools-data-pin-swatch" style="--pin-color:${escapeAttr(pin.color)}" aria-hidden="true"></span>${escapeHtml(pinName(pin, i))}</span>
          ${end(pin, 'max', e.max)}${end(pin, 'min', e.min)}${part}
        </li>`
    }).join('')
    if (!rows.trim()) return ''
    return `
      <p class="tools-data-when-head">${tHtml('tools.data.when.title')}</p>
      <ul class="tools-data-when-list">${rows}</ul>
      <p class="tools-menu-note tools-data-when-note">${tHtml(dated ? 'tools.data.when.note' : 'tools.data.when.noteUndated')}</p>`
  }

  function updateValues(): void {
    const pins = shownPins()
    pins.forEach((pin) => {
      const el = host.querySelector<HTMLElement>(`.tools-data-pin[data-pin="${pin.id}"] .tools-data-pin-value`)
      if (el) el.textContent = rowValue(pin)
      if (!pin.follow) return
      const where = host.querySelector<HTMLElement>(`.tools-data-pin[data-pin="${pin.id}"] .tools-data-pin-where`)
      const text = coords(pin.lat, pin.lon)
      if (where && where.textContent !== text) where.textContent = text
    })
    const chartHost = host.querySelector<HTMLElement>('#tools-data-chart')
    const plot = host.querySelector<HTMLElement>('#tools-data-chart-plot')
    const away = host.querySelector<HTMLElement>('#tools-data-chart-away')
    const note = host.querySelector<HTMLElement>('#tools-data-chart-note')
    if (!chartHost || !plot || !note) return
    const { timed, chart } = timedPins()
    // AYNI: the series can be downloaded once any pin has one.
    const csv = host.querySelector<HTMLElement>('#tools-data-csv')
    if (csv) csv.hidden = timed.length === 0
    // AYNI: trends need dates and a series, and a plan with the research tools.
    const access = deps.statistics?.() ?? 'open'
    const locked = access === 'locked'
    const canTrend = access !== 'hidden' && timed.length > 0 && deps.dated()
    // Emergence needs 30 years (the first pin's window says).
    const first = timed[0]?.series
    const canEmerge = canTrend && !!first && pinEmergence(first) !== 'short'
    const canCorrelate = canTrend && timed.length >= 2
    const names = timed.map((p) => pinName(p, pins.findIndex((q) => q.id === p.id)))
    const units = deps.units?.()
    const sections: Array<[string, string, boolean, boolean, () => string]> = [
      ['tools-data-trend', 'tools-data-trends', canTrend, trendOn, () => trendBlockHtml(timed, names, units)],
      ['tools-data-emergence-btn', 'tools-data-emergence-results', canEmerge, emergenceOn, () => emergenceResultsHtml(timed, names, units, emergenceView(pins))],
      ['tools-data-correlation-btn', 'tools-data-correlation', canCorrelate, correlationOn, () => correlationBlockHtml(timed, names, first ? statsStepMs(first) / (365.25 * 86_400_000) : null)],
    ]
    for (const [btnId, boxId, can, on, html] of sections) {
      const btn = host.querySelector<HTMLElement>(`#${btnId}`)
      if (btn) {
        btn.hidden = !can
        btn.classList.toggle('analyze-locked', locked)
        if (locked) btn.removeAttribute('aria-pressed')
        else btn.setAttribute('aria-pressed', String(on))
      }
      const box = host.querySelector<HTMLElement>(`#${boxId}`)
      if (box) {
        box.hidden = !(can && on && !locked)
        if (!box.hidden) setSection(boxId, html())
      }
    }
    // AYNI: a research record, when one is recording, keeps the results shown (slice 4).
    if (trailOn() && !locked && timed.length) {
      const sig = timed.map((p) => `${p.id}@${p.lat.toFixed(3)},${p.lon.toFixed(3)}`).join(';')
      const shown: Array<['trend' | 'emergence' | 'correlation', boolean, () => (string | number | null)[][] | null]> = [
        ['trend', canTrend && trendOn, () => trendCsvRows(timed, names)],
        ['emergence', canEmerge && emergenceOn, () => emergenceCsvRows(timed, names, emergenceView(pins))],
        ['correlation', canCorrelate && correlationOn, () => correlationCsvRows(timed, names)],
      ]
      for (const [kind, on, rows] of shown) {
        const table = on ? rows() : null
        if (!table) continue
        trail({
          type: 'analysis',
          coalesce: `analysis|${kind}|${sig}`,
          data: {
            kind,
            pins: timed.map((p, i) => ({ name: names[i], lat: p.lat, lon: p.lon })),
            units: units ?? '',
            window: first ? { start: new Date(first.startMs).toISOString(), end: new Date(first.endMs).toISOString() } : null,
            table,
            method: RECORD_METHOD[kind],
            summary: `${kind[0].toUpperCase()}${kind.slice(1)} over ${timed.length} pin${timed.length === 1 ? '' : 's'}: ${table.slice(1).map((r) => r.slice(0, kind === 'correlation' ? 3 : 2).map((c) => (typeof c === 'number' ? Number(c.toPrecision(3)) : c ?? '—')).join(' ')).join('; ')}`,
          },
        })
      }
    }
    // AYNI (owner, 2026-10-18): when each pin was highest and lowest.
    const when = host.querySelector<HTMLElement>('#tools-data-when')
    if (when) {
      const html = whenHtml(pins, timed)
      when.hidden = !html
      // The block is rewritten as frames are read: a date with the focus keeps it.
      const focused = when.contains(document.activeElement) ? (document.activeElement as HTMLElement) : null
      const pinId = focused?.closest<HTMLElement>('[data-pin]')?.dataset.pin
      const kind = focused?.closest<HTMLElement>('[data-when]')?.dataset.when
      setSection('tools-data-when', html)
      if (focused && !focused.isConnected && pinId && kind) when.querySelector<HTMLElement>(`[data-pin="${CSS.escape(pinId)}"] [data-when="${kind}"] [data-when-seek]`)?.focus()
    }
    const controls = host.querySelector<HTMLElement>('#tools-data-emergence-controls')
    if (controls) {
      controls.hidden = !(canEmerge && emergenceOn && !locked)
      if (!controls.hidden && !controls.firstChild) controls.innerHTML = emergenceControlsHtml(units, emergenceView(pins), yearBounds(timed))
    }
    // AYNI: out on the globe, the window draws it and the panel holds its place.
    graphWin?.redraw()
    const here = !!chart && !graphWin
    plot.innerHTML = here ? chartSvg(chart, timed) : ''
    const refs = host.querySelector<HTMLElement>('#tools-data-refs')
    if (refs) {
      const html = here ? refsKeyHtml(chart) : ''
      refs.hidden = !html
      if (lastSection.get('tools-data-refs') !== html) {
        lastSection.set('tools-data-refs', html)
        refs.innerHTML = html
      }
    }
    bindScrub(chartHost, 'panel')
    chartHost.hidden = !here
    if (away) {
      away.hidden = !graphWin
      away.classList.toggle('drop-here', carrying)
      away.style.minHeight = carrying && leftHeight ? `${leftHeight}px` : ''
      const text = away.querySelector<HTMLElement>('.tools-data-chart-away-text')
      if (text) text.textContent = t(carrying ? 'tools.data.graph.dropHere' : 'tools.data.graph.away')
      const back = away.querySelector<HTMLElement>('#tools-data-chart-back')
      if (back) back.hidden = carrying
    }
    note.textContent = graphWin ? '' : chartNote(pins, timed, chart)
    note.hidden = !note.textContent
  }

  /** AYNI: the pin whose name is being typed, and what has been typed. */
  let renaming: string | null = null
  let renameDraft = ''
  /** Set while the rows are replaced, when a field losing the focus is not the viewer leaving it. */
  let rebuilding = false

  /** Save the name typed (or not) and put the name back where the field was; the focus too, when a key ended it. */
  function finishRename(save: boolean, refocus: boolean): void {
    const id = renaming
    if (!id) return
    renaming = null
    const pin = store.list().find((p) => p.id === id)
    if (save && pin) {
      const before = pin.name
      store.rename(id, renameDraft)
      if (pin.name !== before) {
        deps.announce?.(pin.name
          ? t('tools.data.pins.renamed', { name: pin.name })
          : t('tools.data.pins.renamedOwn', { name: pinName(pin, store.list().indexOf(pin)) }))
      }
    }
    // A rename that changed nothing emitted nothing: the name goes back here.
    render()
    if (refocus) document.getElementById(`tools-data-pin-name-${id}`)?.focus()
  }

  function render(): void {
    const r = deps.readability()
    const pins = store.list()
    if (!readable(r) || store.full) setPicking(false)
    // AYNI: a following pin moves every frame; its place is updated in the row (`updateValues`), not rebuilt.
    const canFollow = deps.canFollow?.() ?? true
    const surfaceOn = readable(r) && !!deps.surfaceAvailable?.()
    const surfaceNow = surfaceOn ? deps.surfaceStatus?.() ?? 'all' : 'all'
    const next = `${r}|${picking}|${renaming ?? ''}|${canFollow}:${store.trackShown}|${surfaceOn}:${store.surface}:${surfaceNow}|${pins.map((p) => `${p.id}:${p.tag}:${p.follow}:${p.follow ? '' : `${p.lat}:${p.lon}`}:${p.name ?? ''}`).join(',')}`
    if (next === signature) {
      updateValues()
      return
    }
    signature = next
    const focusedId = (document.activeElement as HTMLElement | null)?.id
    const status = r === 'none' ? t('tools.data.pins.noDataset')
      : r === 'picture' ? t('tools.data.pins.picture')
      : r === 'plan' ? t('tools.data.pins.plan')
      : store.full ? (store.limit < MAX_PINS ? t('tools.data.pins.fullPlan', { count: store.limit }) : t('tools.data.pins.full'))
      : picking ? t('tools.data.pins.picking')
      : ''
    const markup = `
      <div class="tools-data-pins-head">
        <button type="button" class="tools-menu-item tools-data-pin-mode${picking ? ' active' : ''}" id="tools-data-pin-mode" aria-pressed="${picking}"${readable(r) && !store.full ? '' : ' disabled'}>
          <span class="tools-menu-item-check" aria-hidden="true"></span>
          <span class="tools-menu-item-label">${tHtml('tools.data.pins.mode')}</span>
          <span class="tools-data-pin-count">${pins.length}/${store.limit}</span>
        </button>
      </div>
      <p class="tools-menu-note tools-data-pins-status" id="tools-data-pins-status" role="status"${status ? '' : ' hidden'}>${escapeHtml(status)}</p>
      ${(r === 'plan' || (store.full && store.limit < MAX_PINS)) && deps.openPlans ? `<button type="button" class="tools-data-action" id="tools-data-pins-plans" data-reason="${r === 'plan' ? 'data' : 'pins'}">${tHtml('tools.data.pins.seePlans')}</button>` : ''}
      <ul class="tools-data-pin-list" id="tools-data-pin-list"${pins.length ? '' : ' hidden'}>
        ${pins.map((pin, i) => `
        <li class="tools-data-pin" data-pin="${escapeAttr(pin.id)}">
          <span class="tools-data-pin-swatch" style="--pin-color:${escapeAttr(pin.color)}" aria-hidden="true"></span>
          <span class="tools-data-pin-text">
            ${pin.shared
              ? `<span class="tools-data-pin-name is-shared"><span class="tools-data-pin-name-text">${escapeHtml(pinName(pin, i))}</span> <span class="tools-data-pin-shared">${escapeHtml(t('tools.data.pins.shared'))}</span></span>`
              : renaming === pin.id
              ? `<input type="text" class="tools-data-pin-name-input" id="tools-data-pin-name-input-${escapeAttr(pin.id)}" data-rename-input="${escapeAttr(pin.id)}" value="${escapeAttr(renameDraft)}" placeholder="${escapeAttr(pinName({ tag: pin.tag }, i))}" maxlength="${PIN_NAME_MAX}" autocomplete="off" spellcheck="false" enterkeyhint="done" aria-label="${escapeAttr(t('tools.data.pins.renameField', { own: pinName({ tag: pin.tag }, i) }))}">`
              : `<button type="button" class="tools-data-pin-name" id="tools-data-pin-name-${escapeAttr(pin.id)}" data-rename="${escapeAttr(pin.id)}" aria-label="${escapeAttr(t('tools.data.pins.rename', { name: pinName(pin, i) }))}"><span class="tools-data-pin-name-text">${escapeHtml(pinName(pin, i))}</span>${ICON_RENAME}</button>`}
            <span class="tools-data-pin-value">${escapeHtml(rowValue(pin))}</span>
            <span class="tools-data-pin-where">${escapeHtml(coords(pin.lat, pin.lon))}</span>
          </span>
          ${pin.shared ? '' : `<button type="button" class="tools-data-pin-remove" id="tools-data-pin-remove-${escapeAttr(pin.id)}" data-remove="${escapeAttr(pin.id)}" aria-label="${escapeAttr(t('tools.data.pins.remove', { name: pinName(pin, i) }))}">×</button>`}
        </li>`).join('')}
      </ul>
      <div class="tools-data-pin-actions"${readable(r) ? '' : ' hidden'}>
        <button type="button" class="tools-data-action" id="tools-data-extremes">${tHtml('tools.data.pins.extremes')}</button>
        <button type="button" class="tools-data-action" id="tools-data-clear"${pins.length ? '' : ' disabled'}>${tHtml('tools.data.pins.clear')}</button>
      </div>
      ${surfaceOn ? `
      <div class="tools-data-surface" id="tools-data-surface" role="radiogroup" aria-label="${tAttr('tools.data.pins.surface.group')}">
        ${SURFACE_MODES.map((m) => `<button type="button" role="radio" class="tools-data-surface-option${store.surface === m ? ' active' : ''}" id="tools-data-surface-${m}" data-surface="${m}" aria-checked="${store.surface === m}" tabindex="${store.surface === m ? 0 : -1}">${tHtml(surfaceLabelKey(m))}</button>`).join('')}
      </div>
      <p class="tools-menu-note tools-data-surface-note" id="tools-data-surface-note"${surfaceNoteText(surfaceNow) ? '' : ' hidden'}>${escapeHtml(surfaceNoteText(surfaceNow))}</p>` : ''}
      <div class="tools-data-follow" id="tools-data-follow-row"${readable(r) && store.marked && canFollow ? '' : ' hidden'}>
        <button type="button" class="tools-menu-item tools-data-follow-toggle${store.following ? ' active' : ''}" id="tools-data-follow" aria-pressed="${store.following}">
          <span class="tools-menu-item-check" aria-hidden="true"></span>
          <span class="tools-menu-item-label">${tHtml('tools.data.pins.follow')}</span>
        </button>
        <button type="button" class="tools-menu-item tools-data-follow-toggle${store.following && store.trackShown ? ' active' : ''}" id="tools-data-track" aria-pressed="${store.following && store.trackShown}"${store.following ? '' : ' disabled'}>
          <span class="tools-menu-item-check" aria-hidden="true"></span>
          <span class="tools-menu-item-label">${tHtml('tools.data.pins.track')}</span>
        </button>
      </div>
      <div class="tools-data-chart${deps.dock ? ' can-pop' : ''}" id="tools-data-chart" hidden>
        <div class="tools-data-chart-plot" id="tools-data-chart-plot"></div>
        ${deps.dock ? `<button type="button" class="tools-data-chart-pop" id="tools-data-chart-pop" aria-label="${tAttr('tools.data.graph.popOut')}" title="${tAttr('tools.data.graph.popOut')}">${ICON_POP_OUT}</button>` : ''}
      </div>
      <div class="tools-data-refs" id="tools-data-refs" hidden></div>
      ${deps.dock ? `
      <div class="tools-data-chart-away" id="tools-data-chart-away" hidden>
        <span class="tools-data-chart-away-text"></span>
        <button type="button" class="tools-data-action" id="tools-data-chart-back">${tHtml('tools.data.graph.bringBack')}</button>
      </div>` : ''}
      <p class="tools-menu-note tools-data-chart-note" id="tools-data-chart-note" hidden></p>
      <div class="tools-data-when" id="tools-data-when" role="region" aria-label="${tAttr('tools.data.when.title')}" hidden></div>
      <div class="tools-data-graph-actions">
        <button type="button" class="tools-data-action tools-data-trend-btn" id="tools-data-trend" aria-pressed="${trendOn}" hidden>${tHtml('research.trend.button')}</button>
        <button type="button" class="tools-data-action tools-data-trend-btn" id="tools-data-emergence-btn" aria-pressed="${emergenceOn}" hidden>${tHtml('research.emergence.button')}</button>
        <button type="button" class="tools-data-action tools-data-trend-btn" id="tools-data-correlation-btn" aria-pressed="${correlationOn}" hidden>${tHtml('research.correlation.button')}</button>
        <button type="button" class="tools-data-action tools-data-csv" id="tools-data-csv" hidden${deps.csvLocked?.() ? ' data-plan-lock="1"' : ''}>${tHtml(deps.csvLocked?.() ? 'tools.data.csvPlan' : 'tools.data.csv')}</button>
      </div>
      <div class="tools-data-trends" id="tools-data-trends" role="region" aria-label="${tAttr('research.trend.region')}" hidden></div>
      <div class="tools-data-emergence-controls" id="tools-data-emergence-controls" hidden></div>
      <div class="tools-data-trends" id="tools-data-emergence-results" role="region" aria-label="${tAttr('research.emergence.region')}" hidden></div>
      <div class="tools-data-trends" id="tools-data-correlation" role="region" aria-label="${tAttr('research.correlation.region')}" hidden></div>`
    // Replacing the list takes the focus from a name being typed: no reason to save it.
    rebuilding = true
    host.innerHTML = markup
    rebuilding = false

    host.querySelector('#tools-data-csv')?.addEventListener('click', (ev) => {
      ev.stopPropagation()
      downloadPins(ev.currentTarget as HTMLElement)
    })
    lastSection.clear()
    const toggle = (id: string, flip: () => boolean, on: MessageKey, off: MessageKey) => {
      host.querySelector(`#${id}`)?.addEventListener('click', (ev) => {
        ev.stopPropagation()
        if ((deps.statistics?.() ?? 'open') === 'locked') {
          deps.openResearchPlans?.(ev.currentTarget as HTMLElement)
          return
        }
        const now = flip()
        updateValues()
        deps.announce?.(t(now ? on : off))
      })
    }
    toggle('tools-data-trend', () => (trendOn = !trendOn), 'research.trend.on', 'research.trend.off')
    toggle('tools-data-emergence-btn', () => (emergenceOn = !emergenceOn), 'research.emergence.on', 'research.emergence.off')
    toggle('tools-data-correlation-btn', () => (correlationOn = !correlationOn), 'research.correlation.on', 'research.correlation.off')
    // The emergence inputs: a period of whole years inside the series, and an optional noise.
    host.querySelector('#tools-data-emergence-controls')?.addEventListener('change', (ev) => {
      const target = ev.target as HTMLInputElement
      ev.stopPropagation()
      const timed = shownPins().filter((p) => p.series)
      const view = emergenceView(timed)
      const bounds = yearBounds(timed)
      if (target.id === 'tools-data-noise') {
        const v = Number(target.value)
        emergenceNoise = target.value.trim() && v > 0 ? v : null
      } else {
        const start = Number((host.querySelector('#tools-data-period-start') as HTMLInputElement | null)?.value ?? view.period.startYear)
        const end = Number((host.querySelector('#tools-data-period-end') as HTMLInputElement | null)?.value ?? view.period.endYear)
        if (Number.isInteger(start) && Number.isInteger(end) && start >= bounds.minYear && end <= bounds.maxYear && end > start) {
          emergencePeriod = { startYear: start, endYear: end }
        } else {
          target.value = String(target.id.endsWith('start') ? view.period.startYear : view.period.endYear)
        }
      }
      updateValues()
    })
    host.querySelector<HTMLButtonElement>('#tools-data-pins-plans')?.addEventListener('click', (ev) => {
      ev.stopPropagation()
      const button = ev.currentTarget as HTMLElement
      deps.openPlans?.(button, button.dataset.reason === 'pins' ? 'pins' : 'data')
    })
    host.querySelector('#tools-data-pin-mode')?.addEventListener('click', (ev) => {
      ev.stopPropagation()
      setPicking(!picking)
      render()
      deps.announce?.(t(picking ? 'tools.data.pins.picking' : 'tools.data.pins.modeOff'))
    })
    // AYNI (owner, 2026-10-09): a pin's name opens a field in its place.
    host.querySelectorAll<HTMLButtonElement>('[data-rename]').forEach((btn) => {
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation()
        const pin = store.list().find((p) => p.id === btn.dataset.rename)
        if (!pin) return
        renaming = pin.id
        renameDraft = pin.name ?? ''
        render()
        const field = host.querySelector<HTMLInputElement>('[data-rename-input]')
        field?.focus()
        field?.select()
      })
    })
    const field = host.querySelector<HTMLInputElement>('[data-rename-input]')
    if (field) {
      field.addEventListener('input', () => { renameDraft = field.value })
      field.addEventListener('click', (ev) => ev.stopPropagation())
      field.addEventListener('keydown', (ev) => {
        // Not the panel's keys (its Escape closes it) nor the globe's.
        ev.stopPropagation()
        if (ev.key === 'Enter') {
          ev.preventDefault()
          finishRename(true, true)
        } else if (ev.key === 'Escape') {
          ev.preventDefault()
          finishRename(false, true)
        }
      })
      field.addEventListener('blur', () => {
        if (!rebuilding) finishRename(true, false)
      })
    }
    host.querySelectorAll<HTMLButtonElement>('[data-remove]').forEach((btn) => {
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation()
        store.remove(btn.dataset.remove!)
        deps.announce?.(t('tools.data.pins.removed'))
      })
    })
    host.querySelector('#tools-data-extremes')?.addEventListener('click', (ev) => {
      ev.stopPropagation()
      const placed = deps.markExtremes()
      // AYNI: waiting for the land and sea map, the pins come when it does.
      const key = placed ? 'tools.data.pins.extremesPlaced' : deps.markPending?.() ? 'tools.data.pins.surface.pending' : 'tools.data.pins.extremesNone'
      deps.announce?.(t(key))
      if (!placed) {
        const el = host.querySelector<HTMLElement>('#tools-data-pins-status')
        if (el) { el.textContent = t(key); el.hidden = false }
      }
    })
    // AYNI (owner, 2026-10-18): everywhere, land only or sea only; a radio group, arrows move along it.
    const surfaceButtons = [...host.querySelectorAll<HTMLButtonElement>('[data-surface]')]
    const chooseSurface = (mode: SurfaceMode) => {
      if (mode === store.surface) return
      deps.setSurface?.(mode)
      deps.announce?.(t('tools.data.pins.surface.changed', { where: t(surfaceLabelKey(mode)) }))
      document.getElementById(`tools-data-surface-${mode}`)?.focus()
    }
    surfaceButtons.forEach((btn, i) => {
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation()
        chooseSurface(btn.dataset.surface as SurfaceMode)
      })
      btn.addEventListener('keydown', (ev) => {
        const step = ev.key === 'ArrowRight' || ev.key === 'ArrowDown' ? 1 : ev.key === 'ArrowLeft' || ev.key === 'ArrowUp' ? -1 : 0
        if (!step) return
        ev.preventDefault()
        ev.stopPropagation()
        // The bar runs in reading order: right-to-left, the arrows turn round.
        const dir = document.documentElement.dir === 'rtl' && (ev.key === 'ArrowRight' || ev.key === 'ArrowLeft') ? -step : step
        const next = surfaceButtons[(i + dir + surfaceButtons.length) % surfaceButtons.length]
        chooseSurface(next.dataset.surface as SurfaceMode)
      })
    })
    // AYNI (owner, 2026-10-18): the highest and lowest follow the data, or stay ("Freeze here"); and their track.
    host.querySelector('#tools-data-follow')?.addEventListener('click', (ev) => {
      ev.stopPropagation()
      const on = !store.following
      store.setFollow(on)
      deps.announce?.(t(on ? 'tools.data.pins.followOn' : 'tools.data.pins.followOff'))
    })
    host.querySelector('#tools-data-track')?.addEventListener('click', (ev) => {
      ev.stopPropagation()
      store.setTrackShown(!store.trackShown)
      deps.announce?.(t(store.trackShown ? 'tools.data.pins.trackOn' : 'tools.data.pins.trackOff'))
    })
    host.querySelector('#tools-data-clear')?.addEventListener('click', (ev) => {
      ev.stopPropagation()
      store.clear()
      deps.announce?.(t('tools.data.pins.cleared'))
    })
    host.querySelector<HTMLElement>('#tools-data-chart')?.addEventListener('pointerdown', onChartPointerDown)
    host.querySelector<HTMLElement>('#tools-data-refs')?.addEventListener('click', onRefsClick)
    // AYNI (owner, 2026-10-18): a date beside a highest or lowest plays from that frame.
    host.querySelector<HTMLElement>('#tools-data-when')?.addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-when-seek]')
      if (!btn) return
      ev.stopPropagation()
      const ms = Number(btn.dataset.whenSeek)
      const series = shownPins().find((p) => p.series)?.series
      if (!Number.isFinite(ms) || !series) return
      deps.seek?.(ms)
      deps.announce?.(t('tools.data.graph.seeked', { time: pointTime(ms, series) }))
    })
    host.querySelector('#tools-data-chart-pop')?.addEventListener('click', (ev) => {
      ev.stopPropagation()
      popOut()
    })
    host.querySelector('#tools-data-chart-back')?.addEventListener('click', (ev) => {
      ev.stopPropagation()
      graphWin?.dock('button')
    })
    // A rebuild must not drop the focus the viewer had in here.
    if (focusedId) {
      const again = document.getElementById(focusedId) ?? (focusedId.startsWith('tools-data-pin-remove-') ? document.getElementById('tools-data-pin-mode') : null)
      if (again && host.contains(again)) again.focus()
    }
    updateValues()
  }

  host.setAttribute('aria-label', t('tools.data.pins.aria'))
  store.subscribe(render)
  // AYNI: the reference lines' switch, from here, Tools → View or the headset.
  onReferenceLinesChange(updateValues)
  render()
  return {
    refresh: render,
    // AYNI: the headset's Data page turns the same pin mode on and off.
    setPicking(on: boolean) {
      if (on && (!readable(deps.readability()) || store.full)) return
      setPicking(on)
      render()
    },
    isPicking: () => picking,
    graphShown: () => !!graphWin || !!panelChartRect(),
    showGraph(on: boolean) {
      if (on) showGraphOut()
      else graphWin?.dock('close')
    },
    bundleCsvs(): BundleCsv[] {
      const out: BundleCsv[] = []
      const pins = pinsCsv()
      if (pins) out.push({ kind: 'pins', text: pins.text })
      const stats = pinStatisticsCsv()
      if (stats) out.push({ kind: 'pinStatistics', text: stats })
      return out
    },
    statsView() {
      const pins = shownPins()
      return { pins, dated: deps.dated(), emergence: emergenceView(pins), units: deps.units?.() }
    },
    methodsState(): PinsMethodsState {
      const pins = shownPins()
      const timed = pins.filter((p) => p.series)
      const list = timed.map((pin) => ({
        label: pinEnglishName(pin, pins.indexOf(pin)),
        lat: pin.lat,
        lon: pin.lon,
        read: pin.series!.slots.filter((slot) => slot.sampled).length,
        slots: pin.series!.slots.length,
      }))
      const open = statsOpen() && deps.dated() && timed.length > 0
      const view = open && emergenceOn ? emergenceView(pins) : null
      const counts = open ? statsCounts(timed, view) : null
      return {
        pins: list,
        trend: counts && trendOn && counts.trend > 0 && counts.unit ? { pins: counts.trend, unit: counts.unit } : null,
        emergence: counts && view && counts.emergence > 0
          ? { pins: counts.emergence, startYear: view.period.startYear, endYear: view.period.endYear, noise: view.noise, threshold: 2, windowYears: 10, ensemble: view.ensemble }
          : null,
        correlation: counts && correlationOn && counts.pairs > 0 ? { pairs: counts.pairs } : null,
      }
    },
  }
}
